/**
 * KMMO kit components (V4-style navy UI). Plain DOM like meta/ui: every factory takes an options
 * object and an optional `parent`, returns its root element or a small handle with setters.
 * Styles: ./kmmo.css (class prefix `km-`, sizes scale with `--ku`). Import from './index'.
 */
import { el, onPress, svg } from '../ui/dom';
import { type KmRarity, chanceTone, clamp01, deltaTone, fmtCurrency, fmtInt, fmtPercent, plusLabel, pointsAttr, radarAnchor, radarPolygon, radarVertex } from './helpers';
import { type KmIcon, classEmblemSvg, goldCoinSvg, iconUrl, kmIcon } from './icons';

type Parent = HTMLElement | undefined;

/** An icon spec: a kit line icon name, a painted icon URL (contains '/' or '.png'), or an element. */
export type IconSpec = KmIcon | string | Element;

export function iconEl(spec: IconSpec, cls = ''): Element {
  if (typeof spec !== 'string') return spec.cloneNode(true) as Element;
  if (spec.includes('/') || /\.(png|webp|jpg|svg)$/.test(spec)) {
    const img = document.createElement('img');
    img.className = `km-img ${cls}`.trim();
    img.src = spec;
    img.alt = '';
    img.draggable = false;
    img.decoding = 'async';
    return img;
  }
  return kmIcon(spec as KmIcon, cls);
}

// ---------------------------------------------------------------------------
// Buttons
// ---------------------------------------------------------------------------

export interface KmButtonOptions {
  onClick?: (ev: MouseEvent) => void;
  disabled?: boolean;
  /** 'sm' (compact) | 'md' (default) | 'lg' (ornate, side caps). */
  size?: 'sm' | 'md' | 'lg';
  icon?: IconSpec;
  /** Cost shown before the label, separated by a divider: { icon: 'gold' | IconSpec, amount }. */
  cost?: { icon?: IconSpec | 'gold'; amount: number | string; short?: boolean };
  /** Small second line under the label ("150 gold"). */
  sub?: string;
  title?: string;
  className?: string;
  /** Stretch to the container width. */
  block?: boolean;
}

function kmButton(kind: 'primary' | 'secondary' | 'ghost', label: string, o: KmButtonOptions, parent: Parent): HTMLButtonElement {
  const b = el('button', `km-btn km-btn-${kind} km-btn-${o.size ?? 'md'}${o.block ? ' km-block' : ''} ${o.className ?? ''}`.trim(), parent);
  b.type = 'button';
  if (o.cost) {
    const c = el('span', 'km-btn-cost', b);
    c.appendChild(o.cost.icon === undefined || o.cost.icon === 'gold' ? goldCoinSvg() : iconEl(o.cost.icon));
    el('span', 'km-num', c, typeof o.cost.amount === 'number' ? fmtInt(o.cost.amount) : o.cost.amount);
    el('span', 'km-btn-sep', b);
  }
  if (o.icon) b.appendChild(iconEl(o.icon));
  const t = el('span', 'km-btn-label', b);
  el('span', 'km-btn-text', t, label);
  if (o.sub) el('span', 'km-btn-sub', t, o.sub);
  if (o.title) b.title = o.title;
  if (o.disabled) b.disabled = true;
  if (o.onClick) onPress(b, o.onClick);
  return b;
}

/** Gold-orange bevelled call to action ("ENHANCE", "ENTER DUNGEON"). `size: 'lg'` adds ornate caps. */
export function primaryButton(label: string, o: KmButtonOptions = {}, parent?: HTMLElement): HTMLButtonElement {
  return kmButton('primary', label, o, parent);
}

/** Muted indigo button ("Auto-equip", "Salvage", "Back to Lobby"). */
export function secondaryButton(label: string, o: KmButtonOptions = {}, parent?: HTMLElement): HTMLButtonElement {
  return kmButton('secondary', label, o, parent);
}

/** Borderless text button (links, "Cancel" in tight spots). */
export function ghostButton(label: string, o: KmButtonOptions = {}, parent?: HTMLElement): HTMLButtonElement {
  return kmButton('ghost', label, o, parent);
}

/** Square icon-only button (header mail / settings / exit, +/− steppers). */
export function iconButton(icon: IconSpec, o: { onClick?: (ev: MouseEvent) => void; title?: string; dot?: boolean; framed?: boolean; disabled?: boolean } = {}, parent?: HTMLElement): HTMLButtonElement {
  const b = el('button', `km-icon-btn${o.framed ? ' framed' : ''}`, parent);
  b.type = 'button';
  b.appendChild(iconEl(icon));
  if (o.title) {
    b.title = o.title;
    b.setAttribute('aria-label', o.title);
  }
  if (o.dot) el('span', 'km-dot', b);
  if (o.disabled) b.disabled = true;
  if (o.onClick) onPress(b, o.onClick);
  return b;
}

// ---------------------------------------------------------------------------
// Currency bar
// ---------------------------------------------------------------------------

export interface CurrencySpec {
  id: string;
  /** 'gold' (drawn coin) or an IconSpec (painted resource icon URL). */
  icon: 'gold' | IconSpec;
  value: number;
  /** Short word after the number ("gold", "Fangsteel"), omitted by default. */
  label?: string;
  title?: string;
}

export interface CurrencyBarHandle {
  el: HTMLElement;
  /** Update one value by id. */
  set(id: string, value: number): void;
}

/** Top-right currencies (at most 3, per the art direction): icon + number. */
export function currencyBar(items: readonly CurrencySpec[], parent?: HTMLElement): CurrencyBarHandle {
  const root = el('div', 'km-currencies', parent);
  const nums = new Map<string, HTMLElement>();
  for (const c of items.slice(0, 3)) {
    const chip = el('span', 'km-currency', root);
    chip.dataset['id'] = c.id;
    if (c.title) chip.title = c.title;
    chip.appendChild(c.icon === 'gold' ? goldCoinSvg() : iconEl(c.icon, 'km-currency-img'));
    const n = el('span', 'km-num', chip, fmtCurrency(c.value));
    if (c.label) el('span', 'km-currency-label', chip, c.label);
    nums.set(c.id, n);
  }
  return {
    el: root,
    set(id, value) {
      const n = nums.get(id);
      if (n) n.textContent = fmtCurrency(value);
    },
  };
}

// ---------------------------------------------------------------------------
// Screen frame
// ---------------------------------------------------------------------------

export interface ScreenFrameOptions {
  title: string;
  /** Back arrow (top-left). Omit on the lobby. */
  onBack?: () => void;
  /** Small (i) after the title. */
  onInfo?: () => void;
  /** Brand wordmark instead of the title (lobby: "KEYSTONE"). */
  brand?: boolean;
  /** Centre header text (run rewards: dungeon name). */
  center?: string | HTMLElement;
  currencies?: readonly CurrencySpec[];
  /** Header utility icons after the currencies. */
  utilities?: readonly { icon: IconSpec; title: string; onClick: () => void; dot?: boolean }[];
  className?: string;
}

export interface ScreenFrame {
  el: HTMLElement;
  header: HTMLElement;
  title: HTMLElement;
  /** Centre slot of the header (empty unless `center`). */
  center: HTMLElement;
  /** Null without `currencies`. */
  currencies: CurrencyBarHandle | null;
  /** Fills the rest of the screen; `position: relative`, no padding (layouts own it). */
  body: HTMLElement;
  /** Behind the body: a hero stage or art can be mounted here (full-bleed under the header). */
  backdrop: HTMLElement;
}

/**
 * Full-screen KMMO page: navy-to-blue gradient with a vignette, header (back arrow + title
 * top-left, currencies + utility icons top-right), body. Plain background by design.
 */
export function screenFrame(o: ScreenFrameOptions, parent?: HTMLElement): ScreenFrame {
  const root = el('div', `km-screen ${o.className ?? ''}`.trim(), parent);
  const backdrop = el('div', 'km-backdrop', root);
  const header = el('header', 'km-header', root);
  const left = el('div', 'km-header-left', header);
  if (o.onBack) {
    const back = iconButton('back', { onClick: o.onBack, title: 'Back (Esc)' }, left);
    back.classList.add('km-back');
    back.setAttribute('aria-keyshortcuts', 'Escape');
  }
  const title = el('h1', o.brand ? 'km-brand' : 'km-title', left, o.title);
  if (o.onInfo) iconButton('info', { onClick: o.onInfo, title: 'Info' }, left).classList.add('km-info');
  const center = el('div', 'km-header-center', header);
  if (typeof o.center === 'string') el('div', 'km-header-center-text', center, o.center);
  else if (o.center) center.appendChild(o.center);
  const right = el('div', 'km-header-right', header);
  const currencies = o.currencies?.length ? currencyBar(o.currencies, right) : null;
  if (o.utilities?.length) {
    if (currencies) el('span', 'km-vsep', right);
    for (const u of o.utilities) iconButton(u.icon, { title: u.title, onClick: u.onClick, dot: u.dot }, right).classList.add('km-util');
  }
  const body = el('main', 'km-body', root);
  return { el: root, header, title, center, currencies, body, backdrop };
}

// ---------------------------------------------------------------------------
// Panel + divider
// ---------------------------------------------------------------------------

export interface PanelOptions {
  /** Small header line inside the panel ("Odds", "Last crafts"). */
  title?: string;
  /** Gold corner ornaments (default true). */
  ornate?: boolean;
  /** 'glass' (default, translucent navy) | 'solid' | 'flat' (no border, for nested groups). */
  tone?: 'glass' | 'solid' | 'flat';
  className?: string;
}

export interface PanelParts {
  el: HTMLElement;
  body: HTMLElement;
}

/** Framed navy panel: gradient + hairline border + optional gold corner brackets. */
export function panel(o: PanelOptions = {}, parent?: HTMLElement): PanelParts {
  const root = el('section', `km-panel km-panel-${o.tone ?? 'glass'}${o.ornate === false ? '' : ' km-ornate'} ${o.className ?? ''}`.trim(), parent);
  if (o.title) el('h2', 'km-panel-title', root, o.title);
  const body = el('div', 'km-panel-body', root);
  return { el: root, body };
}

/** Thin gold hairline with a centre diamond (section break). */
export function divider(parent?: HTMLElement): HTMLElement {
  const d = el('div', 'km-divider', parent);
  el('i', '', d);
  return d;
}

// ---------------------------------------------------------------------------
// Tabs + side nav
// ---------------------------------------------------------------------------

export interface TabSpec {
  id: string;
  label?: string;
  icon?: IconSpec;
  /** Red notification dot. */
  dot?: boolean;
  disabled?: boolean;
  title?: string;
}

export interface TabsHandle {
  el: HTMLElement;
  active: string;
  set(id: string): void;
}

/**
 * Tabs. `style`: 'icon' (small square icon tabs, gold underline; inventory filters), 'text'
 * (words with a gold underline; "Rotation | Talents"), 'box' (boxed, active filled gold-brown;
 * "Mythic+ | Weekly").
 */
export function iconTabs(items: readonly TabSpec[], o: { active?: string; onChange?: (id: string) => void; style?: 'icon' | 'text' | 'box' } = {}, parent?: HTMLElement): TabsHandle {
  const root = el('div', `km-tabs km-tabs-${o.style ?? 'icon'}`, parent);
  root.setAttribute('role', 'tablist');
  const btns = new Map<string, HTMLButtonElement>();
  const h: TabsHandle = {
    el: root,
    active: o.active ?? items[0]?.id ?? '',
    set(id) {
      h.active = id;
      for (const [k, b] of btns) {
        b.classList.toggle('active', k === id);
        b.setAttribute('aria-selected', String(k === id));
      }
    },
  };
  for (const t of items) {
    const b = el('button', 'km-tab', root);
    b.type = 'button';
    b.setAttribute('role', 'tab');
    if (t.icon) b.appendChild(iconEl(t.icon));
    if (t.label) el('span', 'km-tab-label', b, t.label);
    if (t.title ?? t.label) b.title = t.title ?? t.label ?? '';
    if (t.dot) el('span', 'km-dot', b);
    if (t.disabled) b.disabled = true;
    onPress(b, () => {
      if (h.active === t.id) return;
      h.set(t.id);
      o.onChange?.(t.id);
    });
    btns.set(t.id, b);
  }
  h.set(h.active);
  return h;
}

/**
 * Vertical nav. 'list' = left sub-tabs with a gold bar on the active row (Enhance / Craft /
 * Sets, dungeon list); 'tiles' = framed lobby tiles (Hero / Skills / Forge / Rank).
 */
export function sideNav(items: readonly TabSpec[], o: { active?: string; onChange?: (id: string) => void; variant?: 'list' | 'tiles' } = {}, parent?: HTMLElement): TabsHandle {
  const h = iconTabs(items, { active: o.active, onChange: o.onChange, style: 'text' }, parent);
  h.el.className = `km-sidenav km-sidenav-${o.variant ?? 'list'}`;
  h.el.setAttribute('role', o.variant === 'tiles' ? 'navigation' : 'tablist');
  if (o.variant === 'tiles' && o.active === undefined) h.set('');
  return h;
}

// ---------------------------------------------------------------------------
// Item slot
// ---------------------------------------------------------------------------

export interface ItemSlotOptions {
  rarity?: KmRarity;
  /** Painted icon URL, kit icon name or an element (slot glyph SVG). */
  icon?: IconSpec;
  /** Enhancement level → "+7" badge (top-right). */
  plus?: number;
  /** Stack count (bottom-right). */
  count?: number;
  /** Equipped "E" tag (bottom-right, or top-left with `equippedCorner: 'tl'`). */
  equipped?: boolean;
  equippedCorner?: 'br' | 'tl';
  /** Gold diamond (top-left): tier-set piece. */
  setPiece?: boolean;
  locked?: boolean;
  /** Red "new" dot. */
  isNew?: boolean;
  selected?: boolean;
  /** Dimmed (can't use / not owned). */
  dim?: boolean;
  /** Caption under the slot ("Helm", "Fangsteel 12/12"). */
  caption?: string;
  /** Second caption line (green when `captionTone: 'good'`). */
  caption2?: string;
  captionTone?: 'good' | 'bad' | 'gold';
  /** Ribbon across the bottom ("First timed clear"). */
  ribbon?: string;
  /** 'sm' 64, 'md' 96 (default), 'lg' 128, 'xl' 180 (design px at 1080p). */
  size?: 'sm' | 'md' | 'lg' | 'xl';
  /** Empty placeholder look (faint diamond). */
  empty?: boolean;
  onClick?: (ev: MouseEvent) => void;
  title?: string;
}

export interface ItemSlotHandle {
  /** Wrapper (slot + captions). */
  el: HTMLElement;
  slot: HTMLElement;
  setSelected(on: boolean): void;
}

/** Square gear / material slot on a rarity gradient with badges (+N, count, E, lock, new). */
export function itemSlot(o: ItemSlotOptions = {}, parent?: HTMLElement): ItemSlotHandle {
  const wrap = el('div', `km-slot-wrap km-size-${o.size ?? 'md'}`, parent);
  const slot = el(o.onClick ? 'button' : 'div', 'km-slot', wrap) as HTMLElement;
  if (o.onClick) (slot as HTMLButtonElement).type = 'button';
  const empty = o.empty || (!o.icon && !o.rarity);
  slot.dataset['rarity'] = empty ? 'empty' : (o.rarity ?? 'common');
  if (empty) slot.classList.add('empty');
  if (o.icon) slot.appendChild(iconEl(o.icon, 'km-slot-icon'));
  else if (empty) el('i', 'km-slot-empty-mark', slot);
  const plus = plusLabel(o.plus);
  if (plus) el('span', 'km-badge-plus km-num', slot, plus);
  if (o.count !== undefined && o.count !== null && o.count > 1) el('span', 'km-badge-count km-num', slot, fmtInt(o.count));
  if (o.equipped) el('span', `km-badge-e ${o.equippedCorner === 'tl' ? 'tl' : 'br'}`, slot, 'E');
  if (o.setPiece) el('span', 'km-badge-set', slot);
  if (o.isNew) el('span', 'km-dot', slot);
  if (o.ribbon) el('span', 'km-slot-ribbon', slot, o.ribbon);
  if (o.locked) {
    slot.classList.add('locked');
    el('span', 'km-slot-lock', slot).appendChild(kmIcon('lock'));
  }
  if (o.dim) slot.classList.add('dim');
  if (o.title) slot.title = o.title;
  if (o.caption) el('div', 'km-slot-caption', wrap, o.caption);
  if (o.caption2) el('div', `km-slot-caption2 km-num ${o.captionTone ?? ''}`.trim(), wrap, o.caption2);
  const setSelected = (on: boolean): void => {
    slot.classList.toggle('selected', on);
  };
  setSelected(!!o.selected);
  if (o.onClick) onPress(slot, o.onClick);
  return { el: wrap, slot, setSelected };
}

/** Round framed icon (skills, affixes, talent picks). `ring: 'gold'` for the selected / central one. */
export function roundIcon(icon: IconSpec, o: { size?: 'sm' | 'md' | 'lg' | 'xl'; ring?: 'gold' | 'steel'; caption?: string; locked?: boolean; onClick?: (ev: MouseEvent) => void; title?: string; dim?: boolean } = {}, parent?: HTMLElement): HTMLElement {
  const wrap = el('div', `km-round-wrap km-size-${o.size ?? 'md'}`, parent);
  const r = el(o.onClick ? 'button' : 'div', `km-round ${o.ring ?? 'steel'}${o.dim ? ' dim' : ''}`, wrap) as HTMLElement;
  if (o.onClick) {
    (r as HTMLButtonElement).type = 'button';
    onPress(r, o.onClick);
  }
  r.appendChild(o.locked ? kmIcon('lock', 'km-round-lock') : iconEl(icon, 'km-round-icon'));
  if (o.title) r.title = o.title;
  if (o.caption) el('div', 'km-round-caption', wrap, o.caption);
  return wrap;
}

/** The skill ring: a big centre icon with up to 8 round icons on a thin gold circle. */
export function skillRing(center: IconSpec, around: readonly { icon: IconSpec; title?: string; onClick?: () => void; active?: boolean }[], o: { onCenter?: () => void } = {}, parent?: HTMLElement): HTMLElement {
  const root = el('div', 'km-skill-ring', parent);
  el('i', 'km-skill-ring-circle', root);
  const n = Math.max(1, around.length);
  around.forEach((a, i) => {
    const p = radarVertex(i, n, 50, 50, 41);
    const w = roundIcon(a.icon, { size: 'sm', ring: a.active ? 'gold' : 'steel', title: a.title, onClick: a.onClick }, root);
    w.classList.add('km-skill-ring-item');
    w.style.left = `${p.x}%`;
    w.style.top = `${p.y}%`;
  });
  const c = roundIcon(center, { size: 'lg', ring: 'gold', onClick: o.onCenter }, root);
  c.classList.add('km-skill-ring-center');
  return root;
}

// ---------------------------------------------------------------------------
// Stats
// ---------------------------------------------------------------------------

export interface StatRow {
  icon?: IconSpec;
  label: string;
  value: string | number;
  /** Colour the value: 'good' green, 'bad' red, 'gold', 'blue'. */
  tone?: 'good' | 'bad' | 'gold' | 'blue';
}

/** Compact stat list (icon + label left, value right, hairline separators). */
export function statList(rows: readonly StatRow[], parent?: HTMLElement): HTMLElement {
  const root = el('dl', 'km-stats', parent);
  for (const r of rows) {
    const row = el('div', 'km-stat', root);
    const dt = el('dt', '', row);
    if (r.icon) dt.appendChild(iconEl(r.icon));
    el('span', '', dt, r.label);
    el('dd', `km-num${r.tone ? ` km-t-${r.tone}` : ''}`, row, typeof r.value === 'number' ? fmtInt(r.value) : r.value);
  }
  return root;
}

export interface DiffRow {
  label: string;
  from: number | string;
  to: number | string;
}

/** "Item Level 235 → 240" rows; the new value is green when it went up (numbers compared). */
export function statDiff(rows: readonly DiffRow[], o: { head?: [string, string] } = {}, parent?: HTMLElement): HTMLElement {
  const root = el('div', 'km-diff', parent);
  if (o.head) {
    const h = el('div', 'km-diff-head km-num', root);
    el('span', '', h, o.head[0]);
    el('span', 'km-diff-arrow', h, '→');
    el('span', 'km-t-gold', h, o.head[1]);
  }
  for (const r of rows) {
    const row = el('div', 'km-diff-row', root);
    el('span', 'km-diff-label', row, r.label);
    el('span', 'km-num', row, typeof r.from === 'number' ? fmtInt(r.from) : r.from);
    el('span', 'km-diff-arrow', row, '→');
    const tone = typeof r.from === 'number' && typeof r.to === 'number' ? deltaTone(r.from, r.to) : 'up';
    el('span', `km-num km-diff-to ${tone}`, row, typeof r.to === 'number' ? fmtInt(r.to) : r.to);
  }
  return root;
}

// ---------------------------------------------------------------------------
// Radar chart
// ---------------------------------------------------------------------------

export interface RadarAxis {
  label: string;
  /** 0..1 */
  value: number;
}

const SVGNS = 'http://www.w3.org/2000/svg';
function s<K extends keyof SVGElementTagNameMap>(tag: K, attrs: Record<string, string | number>, parent?: Element): SVGElementTagNameMap[K] {
  const e = document.createElementNS(SVGNS, tag);
  for (const k in attrs) e.setAttribute(k, String(attrs[k]));
  parent?.appendChild(e);
  return e;
}

export interface RadarHandle {
  el: HTMLElement;
  set(values: readonly number[]): void;
}

/** SVG pentagon (any n ≥ 3) radar: 3 guide rings, spokes, green fill, vertex dots, labels. */
export function radarChart(axes: readonly RadarAxis[], o: { rings?: number } = {}, parent?: HTMLElement): RadarHandle {
  const root = el('div', 'km-radar', parent);
  const n = Math.max(3, axes.length);
  const W = 300;
  const cx = W / 2;
  const cy = W / 2 + 6;
  const R = 92;
  const sv = s('svg', { viewBox: `0 0 ${W} ${W}`, class: 'km-radar-svg', role: 'img', 'aria-label': axes.map((a) => `${a.label} ${Math.round(clamp01(a.value) * 100)}%`).join(', ') }, root);
  const rings = o.rings ?? 3;
  for (let k = rings; k >= 1; k--) {
    const pts = Array.from({ length: n }, (_, i) => radarVertex(i, n, cx, cy, (R * k) / rings));
    s('polygon', { points: pointsAttr(pts), class: k === rings ? 'km-radar-outer' : 'km-radar-ring' }, sv);
  }
  for (let i = 0; i < n; i++) {
    const p = radarVertex(i, n, cx, cy, R);
    s('line', { x1: cx, y1: cy, x2: p.x, y2: p.y, class: 'km-radar-spoke' }, sv);
  }
  const area = s('polygon', { class: 'km-radar-area', points: '' }, sv);
  const dots = Array.from({ length: n }, () => s('circle', { r: 3.2, class: 'km-radar-dot' }, sv));
  axes.forEach((a, i) => {
    const p = radarVertex(i, n, cx, cy, R + 16);
    const t = s('text', { x: p.x, y: p.y + (i === 0 ? -4 : 6), 'text-anchor': radarAnchor(i, n), class: 'km-radar-label' }, sv);
    t.textContent = a.label;
  });
  const set = (values: readonly number[]): void => {
    const pts = radarPolygon(Array.from({ length: n }, (_, i) => values[i] ?? 0), cx, cy, R);
    area.setAttribute('points', pointsAttr(pts));
    pts.forEach((p, i) => {
      dots[i]!.setAttribute('cx', String(p.x));
      dots[i]!.setAttribute('cy', String(p.y));
    });
  };
  set(axes.map((a) => a.value));
  return { el: root, set };
}

// ---------------------------------------------------------------------------
// Class crest
// ---------------------------------------------------------------------------

/** Diamond-framed class emblem: blue normal, gold when `selected`. Label underneath. */
export function classCrest(classId: string, o: { label?: string; selected?: boolean; size?: 'sm' | 'md' | 'lg'; onClick?: (ev: MouseEvent) => void; icon?: IconSpec; title?: string } = {}, parent?: HTMLElement): HTMLElement & { setSelected?: (on: boolean) => void } {
  const wrap = el(o.onClick ? 'button' : 'div', `km-crest km-size-${o.size ?? 'md'}${o.selected ? ' selected' : ''}`, parent) as HTMLElement & { setSelected?: (on: boolean) => void };
  if (o.onClick) {
    (wrap as HTMLButtonElement).type = 'button';
    onPress(wrap, o.onClick);
  }
  const d = el('span', 'km-crest-diamond', wrap);
  el('i', 'km-crest-inner', d);
  d.appendChild(o.icon ? iconEl(o.icon, 'km-crest-icon') : classEmblemSvg(classId));
  if (o.label) el('span', 'km-crest-label', wrap, o.label);
  if (o.title) wrap.title = o.title;
  wrap.setSelected = (on: boolean) => wrap.classList.toggle('selected', on);
  return wrap;
}

// ---------------------------------------------------------------------------
// Progress, percent badge, toggle
// ---------------------------------------------------------------------------

export interface ProgressHandle {
  el: HTMLElement;
  set(value: number, max?: number): void;
}

export interface ProgressOptions {
  value: number;
  max: number;
  /** 'gold' (weekly / mileage), 'blue' (EXP), 'green' (resonance, venom), 'red'. */
  tone?: 'gold' | 'blue' | 'green' | 'red';
  /** Label left of the bar. */
  label?: string;
  /** Text right of the bar: 'fraction' "2/4", 'percent' "75%", or a custom string; none by default. */
  value_text?: 'fraction' | 'percent' | string;
  /** Split into N segments (forge pity: "Epic guaranteed in 4 crafts"). */
  segments?: number;
  /** Chest/reward icon at the end of the bar. */
  reward?: IconSpec;
  thin?: boolean;
}

/** Thin bevelled bar on a dark track, optional label / value / segments / end reward. */
export function progressBar(o: ProgressOptions, parent?: HTMLElement): ProgressHandle {
  const root = el('div', `km-progress km-tone-${o.tone ?? 'gold'}${o.thin ? ' thin' : ''}`, parent);
  if (o.label) el('span', 'km-progress-label', root, o.label);
  const track = el('div', `km-progress-track${o.segments ? ' segmented' : ''}`, root);
  const fill = el('div', 'km-progress-fill', track);
  if (o.segments && o.segments > 1) {
    track.style.setProperty('--segs', String(o.segments));
    for (let i = 1; i < o.segments; i++) el('i', 'km-progress-tick', track).style.left = `${(i / o.segments) * 100}%`;
  }
  const txt = o.value_text ? el('span', 'km-progress-value km-num', root) : null;
  if (o.reward) {
    const r = el('span', 'km-progress-reward', root);
    r.appendChild(iconEl(o.reward));
  }
  let max = o.max;
  const set = (v: number, m?: number): void => {
    if (m !== undefined) max = m;
    const f = max > 0 ? clamp01(v / max) : 0;
    fill.style.width = `${(f * 100).toFixed(2)}%`;
    if (txt) txt.textContent = o.value_text === 'fraction' ? `${fmtInt(v)}/${fmtInt(max)}` : o.value_text === 'percent' ? fmtPercent(f) : (o.value_text ?? '');
  };
  set(o.value);
  return { el: root, set };
}

export interface PercentBadgeHandle {
  el: HTMLElement;
  set(frac: number, note?: string): void;
}

/**
 * Success chance: "Success Rate 70%" with the number coloured by band (green / amber / red),
 * and an optional small note under it ("+10% after a failed attempt"). `compact` = pill.
 */
export function percentBadge(fracValue: number, o: { label?: string; note?: string; compact?: boolean; digits?: number } = {}, parent?: HTMLElement): PercentBadgeHandle {
  const root = el('div', `km-percent${o.compact ? ' compact' : ''}`, parent);
  const line = el('div', 'km-percent-line', root);
  if (o.label !== '') el('span', 'km-percent-label', line, o.label ?? 'Success Rate');
  const num = el('span', 'km-percent-num km-num', line);
  const note = el('div', 'km-percent-note', root);
  const set = (f: number, n?: string): void => {
    num.textContent = fmtPercent(clamp01(f), o.digits ?? 0);
    num.dataset['tone'] = chanceTone(f);
    if (n !== undefined) note.textContent = n;
    note.hidden = !note.textContent;
  };
  note.textContent = o.note ?? '';
  set(fracValue);
  return { el: root, set };
}

/** Pill switch ("Add Venom (+luck)"). */
export function toggle(o: { on: boolean; onChange?: (on: boolean) => void; label?: string; icon?: IconSpec; disabled?: boolean }, parent?: HTMLElement): { el: HTMLElement; set(on: boolean): void } {
  const root = el('label', 'km-toggle', parent);
  if (o.icon) root.appendChild(iconEl(o.icon, 'km-toggle-icon'));
  if (o.label) el('span', 'km-toggle-label', root, o.label);
  const b = el('button', 'km-switch', root);
  b.type = 'button';
  b.setAttribute('role', 'switch');
  el('i', '', b);
  let on = o.on;
  const set = (v: boolean): void => {
    on = v;
    b.classList.toggle('on', v);
    b.setAttribute('aria-checked', String(v));
  };
  set(on);
  if (o.disabled) b.disabled = true;
  onPress(b, () => {
    set(!on);
    o.onChange?.(on);
  });
  return { el: root, set };
}

/** Number stepper "− +7 +" (key level). */
export function stepper(o: { value: number; min: number; max: number; format?: (v: number) => string; onChange?: (v: number) => void }, parent?: HTMLElement): { el: HTMLElement; set(v: number): void } {
  const root = el('div', 'km-stepper', parent);
  let v = o.value;
  const dec = iconButton('minus', { framed: true, title: 'Lower' }, root);
  const num = el('span', 'km-stepper-num km-num', root);
  const inc = iconButton('plus', { framed: true, title: 'Higher' }, root);
  const set = (x: number): void => {
    v = Math.min(o.max, Math.max(o.min, Math.round(x)));
    num.textContent = o.format ? o.format(v) : String(v);
    dec.disabled = v <= o.min;
    inc.disabled = v >= o.max;
  };
  onPress(dec, () => {
    set(v - 1);
    o.onChange?.(v);
  });
  onPress(inc, () => {
    set(v + 1);
    o.onChange?.(v);
  });
  set(v);
  return { el: root, set };
}

// ---------------------------------------------------------------------------
// Item card (tooltip / detail panel)
// ---------------------------------------------------------------------------

export interface ItemCardData {
  name: string;
  rarity: KmRarity;
  /** "Weapon", "Helm · Tier". */
  slotLabel?: string;
  ilvl?: number;
  plus?: number;
  icon?: IconSpec;
  /** Main stat lines ("Power 8,420"). */
  stats?: readonly { label: string; value: string; tone?: 'good' | 'bad' | 'gold' | 'blue' }[];
  /** Effect / legendary text paragraphs. */
  effects?: readonly string[];
  set?: { name: string; have: number; bonuses: readonly { pieces: number; text: string }[] };
  /** Muted footer ("Click to equip"). */
  hint?: string;
}

/** Item tooltip card: rarity header band (icon, name, slot · ilvl), stats, effects, set bonus. */
export function itemCard(d: ItemCardData, parent?: HTMLElement): HTMLElement {
  const root = el('div', 'km-card', parent);
  root.dataset['rarity'] = d.rarity;
  const head = el('div', 'km-card-head', root);
  if (d.icon) itemSlot({ icon: d.icon, rarity: d.rarity, plus: d.plus, size: 'sm' }, head);
  const t = el('div', 'km-card-titles', head);
  el('div', 'km-card-name', t, d.name + (d.plus ? ` +${d.plus}` : ''));
  const meta = [d.slotLabel, d.ilvl ? `Item Level ${d.ilvl}` : ''].filter(Boolean).join('  ·  ');
  if (meta) el('div', 'km-card-meta', t, meta);
  if (d.stats?.length) {
    const st = el('div', 'km-card-stats', root);
    for (const x of d.stats) {
      const r = el('div', 'km-card-stat', st);
      el('span', '', r, x.label);
      el('span', `km-num${x.tone ? ` km-t-${x.tone}` : ''}`, r, x.value);
    }
  }
  for (const e of d.effects ?? []) el('p', 'km-card-effect', root, e);
  if (d.set) {
    const st = el('div', 'km-card-set', root);
    el('div', 'km-card-set-name', st, `${d.set.name}  ${d.set.have}/${Math.max(...d.set.bonuses.map((b) => b.pieces), d.set.have)}`);
    for (const b of d.set.bonuses) el('div', `km-card-set-bonus${d.set.have >= b.pieces ? ' on' : ''}`, st, `(${b.pieces}) ${b.text}`);
  }
  if (d.hint) el('div', 'km-card-hint', root, d.hint);
  return root;
}

// ---------------------------------------------------------------------------
// Hover card layer
// ---------------------------------------------------------------------------

let hoverLayer: HTMLElement | null = null;
let hoverOwner: HTMLElement | null = null;

function layer(): HTMLElement {
  if (!hoverLayer || !hoverLayer.isConnected) {
    hoverLayer = el('div', 'km-hover', document.body);
    hoverLayer.setAttribute('role', 'tooltip');
  }
  return hoverLayer;
}

export function hideHoverCard(): void {
  hoverOwner = null;
  hoverLayer?.classList.remove('show');
}

/** Show `content()` beside `target` on hover / focus (built lazily each time). Returns a detach fn. */
export function attachHoverCard(target: HTMLElement, content: () => HTMLElement | string | null): () => void {
  const show = (): void => {
    const c = content();
    if (!c) return;
    const l = layer();
    l.replaceChildren(typeof c === 'string' ? el('div', 'km-hover-text', undefined, c) : c);
    hoverOwner = target;
    l.classList.add('show');
    const r = target.getBoundingClientRect();
    const w = l.offsetWidth;
    const h = l.offsetHeight;
    let x = r.right + 12;
    if (x + w > innerWidth - 8) x = r.left - 12 - w;
    if (x < 8) x = Math.max(8, Math.min(innerWidth - w - 8, r.left));
    const y = Math.max(8, Math.min(innerHeight - h - 8, r.top));
    l.style.transform = `translate(${Math.round(x)}px, ${Math.round(y)}px)`;
  };
  const hide = (): void => {
    if (hoverOwner === target) hideHoverCard();
  };
  target.addEventListener('pointerenter', show);
  target.addEventListener('pointerleave', hide);
  target.addEventListener('focus', show);
  target.addEventListener('blur', hide);
  return () => {
    hide();
    target.removeEventListener('pointerenter', show);
    target.removeEventListener('pointerleave', hide);
    target.removeEventListener('focus', show);
    target.removeEventListener('blur', hide);
  };
}

// ---------------------------------------------------------------------------
// Modal, confirm, toast
// ---------------------------------------------------------------------------

export interface KmModalHandle {
  el: HTMLElement;
  body: HTMLElement;
  footer: HTMLElement;
  close(): void;
}

/** Centered ornate dialog over a dimmed backdrop. Esc / backdrop click closes (unless `locked`). */
export function kmModal(title: string, o: { onClose?: () => void; wide?: boolean; locked?: boolean } = {}): KmModalHandle {
  const back = el('div', 'km-modal-back', document.body);
  const box = el('div', `km-modal km-panel km-panel-solid km-ornate${o.wide ? ' wide' : ''}`, back);
  box.setAttribute('role', 'dialog');
  box.setAttribute('aria-modal', 'true');
  box.setAttribute('aria-label', title);
  el('h2', 'km-modal-title', box, title);
  divider(box);
  const body = el('div', 'km-modal-body', box);
  const footer = el('footer', 'km-modal-footer', box);
  let closed = false;
  const onKey = (e: KeyboardEvent): void => {
    if (e.key === 'Escape' && !o.locked) {
      e.stopPropagation();
      close();
    }
  };
  const close = (): void => {
    if (closed) return;
    closed = true;
    window.removeEventListener('keydown', onKey, true);
    back.classList.add('out');
    setTimeout(() => back.remove(), 160);
    o.onClose?.();
  };
  if (!o.locked) {
    const x = iconButton('close', { title: 'Close', onClick: close }, box);
    x.classList.add('km-modal-x');
    back.addEventListener('pointerdown', (e) => {
      if (e.target === back) close();
    });
  }
  window.addEventListener('keydown', onKey, true);
  return { el: box, body, footer, close };
}

/** Confirm: message, secondary "Cancel" + primary confirm (optionally with a cost). */
export function kmConfirm(title: string, message: string, confirmLabel: string, onConfirm: () => void, o: { cost?: KmButtonOptions['cost']; cancelLabel?: string } = {}): KmModalHandle {
  const m = kmModal(title);
  el('p', 'km-modal-text', m.body, message);
  secondaryButton(o.cancelLabel ?? 'Cancel', { onClick: () => m.close() }, m.footer);
  primaryButton(confirmLabel, {
    cost: o.cost,
    onClick: () => {
      m.close();
      onConfirm();
    },
  }, m.footer);
  return m;
}

let toastHost: HTMLElement | null = null;

/** Transient centred banner near the top ("Enhanced to +5", "Not enough Fangsteel"). */
export function kmToast(message: string, kind: 'info' | 'success' | 'error' = 'info', ms = 2400): HTMLElement | null {
  if (typeof document === 'undefined') return null;
  if (!toastHost || !toastHost.isConnected) toastHost = el('div', 'km-toasts', document.body);
  const t = el('div', `km-toast ${kind}`, toastHost);
  el('span', 'km-toast-text', t, message);
  t.setAttribute('role', kind === 'error' ? 'alert' : 'status');
  setTimeout(() => t.classList.add('out'), ms);
  setTimeout(() => t.remove(), ms + 350);
  return t;
}

/** Small inline SVG helper for one-off glyphs (re-exported for screens). */
export { svg };
